/* eslint-disable max-lines */
import { initialPhase1RunState } from './initialPhase1RunState'
import { appendToolActivity, closeMatchingActivity } from './streamEventActivity'
import { toBrowserPreviewBlock, toTypedBlock } from './streamEventBlocks'
import { appendTextPart, partsAfterRestart, upsertAccessPart } from './responseParts'
import { mergeSnapshotRows } from './timelineRows'
import { toWorkStatusSnapshot } from './workStatusThoughts'
import { mergePublicPhases, publicProgressPhase } from './publicTimelineState'

export function streamEventReducer(state, event) {
  if (!event || typeof event !== 'object') return state
  const seq = typeof event.sequence === 'number' ? event.sequence : null

  if (event.type !== 'start' && seq !== null && seq <= state.lastSequence) {
    return state
  }

  const next = { ...state, lastSequence: seq ?? state.lastSequence }

  switch (event.type) {
    case 'start':
      return {
        ...initialPhase1RunState(event.run_id ?? state.runId),
        status: 'streaming',
        lastSequence: seq ?? -1,
        mock: Boolean(event.mock),
        memoryIds: event.memory_ids ?? [],
        messageId: event.message_id ?? null,
        assistantMessageId: event.assistant_message_id ?? null,
      }

    case 'chunk':
      return {
        ...next,
        status: 'streaming',
        text: state.text + (event.text ?? ''),
        responseParts: appendTextPart(state.responseParts, event),
      }

    case 'restart':
      // Hermes is replaying a provider call after it had already streamed
      // text. Keep tool activity visible, but replace the failed attempt's
      // answer instead of concatenating both attempts.
      return {
        ...next,
        status: 'streaming',
        text: '',
        citations: [],
        responseParts: partsAfterRestart(state.responseParts, event),
        blocks: state.blocks.filter((block) => {
          const attemptId = event.attempt_id ?? null
          return attemptId ? block?.attemptId !== attemptId : false
        }),
      }

    case 'tool': {
      // A live-preview URL rides a `tool` event but has no completion, so it is
      // a block only — observing it would leave a row spinning forever.
      const preview = toBrowserPreviewBlock(event)
      if (preview) return { ...next, status: 'streaming', blocks: [...state.blocks, preview] }
      return { ...next, status: 'streaming', activity: appendToolActivity(state.activity, event, seq) }
    }

    case 'tool_result': {
      const publicPhase = publicProgressPhase(event)
      if (publicPhase) {
        return {
          ...next,
          status: 'streaming',
          publicTimeline: mergePublicPhases(state.publicTimeline, [publicPhase]),
        }
      }
      const workStatus = toWorkStatusSnapshot(event.payload)
      if (workStatus) {
        if (workStatus.revision <= state.workStatusRevision) return next
        return {
          ...next,
          status: 'streaming',
          workStatus,
          workStatusRevision: workStatus.revision,
          // The snapshot is authoritative for rows it knows and re-parents them
          // by id; rows it has not caught up with are kept, not discarded.
          activity: mergeSnapshotRows(state.activity, workStatus),
        }
      }
      // A work-status snapshot is a server projection, not an ordinary tool
      // activity. Invalid or empty snapshots must not invent a generic entry.
      if (event.tool === 'work_status') return { ...next, status: 'streaming' }
      // Typed cards are output, not work: the tool call that produced them is
      // already a timeline row, and only the card survives a reload.
      const block = toTypedBlock(event)
      if (block?.type === 'integration_access_request') {
        return {
          ...next,
          status: 'streaming',
          responseParts: upsertAccessPart(state.responseParts, block, event),
        }
      }
      if (block) return { ...next, status: 'streaming', blocks: [...state.blocks, block] }
      return { ...next, status: 'streaming', activity: closeMatchingActivity(state.activity, event) }
    }

    case 'done':
      return {
        ...next,
        status: 'done',
        // `done` is terminal metadata, not a competing answer. Retaining the
        // chunks also protects users while an older API instance is draining.
        text: state.text,
        citations: event.citations ?? [],
        memoryIds: event.memory_ids ?? state.memoryIds,
        messageId: event.message_id ?? state.messageId,
      }

    case 'error':
      return {
        ...next,
        status: 'error',
        error: { message: event.message ?? 'Something went wrong', code: event.code ?? null },
      }

    default:
      return next
  }
}

export { initialPhase1RunState }
